import { validateSnapshot } from "../models/snapshot"

// A return to the foreground this soon after a good download reuses it.
const FOREGROUND_MIN_GAP_MS = 30 * 1000

// The download progress bar redraws at most this often.
const PROGRESS_THROTTLE_MS = 150

export const SYNC_PHASE = { LOADING: "loading", IDLE: "idle", SYNCING: "syncing" }
export const SYNC_ERROR = { NETWORK: "network", SERVER: "server", SCHEMA: "schema" }

const MESSAGES = {
  network: "Couldn't reach Vantero. Showing the menu saved on this device.",
  schema: "This version of the app can't read the latest menu. Update the app.",
  shape: "The menu download was incomplete. Try again."
}

export const INITIAL_SYNC_STATE = {
  phase: SYNC_PHASE.LOADING,
  hasData: false,
  lastSyncedAt: null,
  lastError: null,
  needsAppUpdate: false,
  progress: null
}

// Downloads the whole menu when the app opens, returns to the foreground or the user refreshes.
// A good download replaces the stored copy; a failed one leaves it untouched.
export default class MenuSyncEngine {
  constructor({ key, restaurantId, language, store, fetchSnapshot, onRecord, onState, onSynced }) {
    this.key = key
    this.restaurantId = restaurantId
    this.language = language
    this.store = store
    this.fetchSnapshot = fetchSnapshot
    this.onRecord = onRecord ?? (() => {})
    this.onState = onState ?? (() => {})
    this.onSynced = onSynced ?? (() => {})

    this.state = INITIAL_SYNC_STATE
    this.record = null
    this.inFlight = null
    this.controller = null
    this.stopped = false

    this.handleVisibility = () => {
      if (document.visibilityState === "visible") this.syncNow({ reason: "foreground" })
    }
  }

  setState(patch) {
    this.state = { ...this.state, ...patch }
    if (!this.stopped) this.onState(this.state)
  }

  async start() {
    const generation = this.store.currentGeneration()
    const record = await this.store.readSnapshotRecord(this.key)
    if (this.stopped) return

    if (record?.snapshot && generation === this.store.currentGeneration()) {
      this.record = record
      this.onRecord(record)
    }
    this.setState({
      phase: SYNC_PHASE.IDLE,
      hasData: Boolean(this.record),
      lastSyncedAt: this.record?.savedAt ?? null
    })

    document.addEventListener("visibilitychange", this.handleVisibility)
    this.syncNow({ reason: "start" })
  }

  stop() {
    this.stopped = true
    document.removeEventListener("visibilitychange", this.handleVisibility)
    this.controller?.abort()
  }

  // One download at a time; every caller gets the same in-flight promise.
  syncNow({ reason = "manual" } = {}) {
    if (this.stopped || this.state.phase === SYNC_PHASE.LOADING) return Promise.resolve()
    if (this.inFlight) return this.inFlight
    if (this.state.needsAppUpdate && reason !== "manual") return Promise.resolve()

    const syncedRecently =
      this.state.lastSyncedAt && Date.now() - this.state.lastSyncedAt < FOREGROUND_MIN_GAP_MS
    if (reason === "foreground" && syncedRecently && !this.state.lastError) return Promise.resolve()

    if (navigator.onLine === false) {
      this.setState({
        lastError: { kind: SYNC_ERROR.NETWORK, message: MESSAGES.network, at: Date.now() }
      })
      return Promise.resolve()
    }

    this.inFlight = this.run().finally(() => {
      this.inFlight = null
    })
    return this.inFlight
  }

  async run() {
    const generation = this.store.currentGeneration()
    const controller = new AbortController()
    this.controller = controller
    this.setState({ phase: SYNC_PHASE.SYNCING, progress: null })

    let lastProgressAt = 0
    const onProgress = (progress) => {
      const now = Date.now()
      if (this.stopped || now - lastProgressAt < PROGRESS_THROTTLE_MS) return
      lastProgressAt = now
      this.setState({ progress })
    }

    try {
      const snapshot = await this.fetchSnapshot({
        restaurantId: this.restaurantId,
        language: this.language,
        signal: controller.signal,
        onProgress
      })
      if (this.stopped) return

      const check = validateSnapshot(snapshot)
      if (!check.ok) {
        this.fail(
          check.reason === "schema"
            ? { kind: SYNC_ERROR.SCHEMA, message: MESSAGES.schema }
            : { kind: SYNC_ERROR.SERVER, message: MESSAGES.shape }
        )
        return
      }
      // A response for another restaurant means the request outlived a switch.
      if (String(snapshot.restaurant_id) !== String(this.restaurantId)) {
        this.fail({ kind: SYNC_ERROR.SERVER, message: MESSAGES.shape })
        return
      }

      const record = {
        key: this.key,
        restaurantId: this.restaurantId,
        language: this.language,
        savedAt: Date.now(),
        snapshot
      }
      await this.store.writeSnapshotRecord(record, generation)
      if (this.stopped || generation !== this.store.currentGeneration()) return
      this.record = record
      this.onRecord(record)
      await this.store.pruneSnapshotRecords(this.key)
      this.succeed(record.savedAt)
    } catch (error) {
      if (this.stopped || isCanceled(error)) return
      this.fail(
        error?.status
          ? { kind: SYNC_ERROR.SERVER, message: error.message }
          : { kind: SYNC_ERROR.NETWORK, message: MESSAGES.network }
      )
    } finally {
      if (this.controller === controller) this.controller = null
    }
  }

  succeed(savedAt) {
    this.setState({
      phase: SYNC_PHASE.IDLE,
      hasData: true,
      lastSyncedAt: savedAt,
      lastError: null,
      needsAppUpdate: false,
      progress: null
    })
    this.onSynced()
  }

  // No automatic retry: the next foreground or refresh tries again.
  fail(error) {
    this.setState({
      phase: SYNC_PHASE.IDLE,
      lastError: { ...error, at: Date.now() },
      needsAppUpdate: error.kind === SYNC_ERROR.SCHEMA,
      progress: null
    })
  }
}

function isCanceled(error) {
  return (
    error?.name === "CanceledError" ||
    error?.cause?.code === "ERR_CANCELED" ||
    error?.cause?.name === "CanceledError"
  )
}
